import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { Alert, Button, Form, Input, Popconfirm, Row, Space, Table, Tooltip, Skeleton, message } from "antd";
import { EditableCell } from "../../../core/commons/editable-cell/editable.cell";
import { CartCreateCalculatedPrice } from "../../../../models/cart/create/cart.create";
import { Amount } from "../../../../models/amount/amount";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping, faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { useRequiredLayers } from "../../../../utils/context";
import { CartCreateStore, CartStoreContext } from "../cart.create.store";
import { CartCreateController, CartCreateControllerContext } from "../cart.create.controller";
import { CartCreateOrderQuantity } from "../../../../models/cart/create/quantity/cart.create.order.quantity";
import { CartCreateTableLastRecord, CartCreateTableRecord } from "../../../../models/cart/create/records/records";
import { CartCreateArticleModal } from "../cart-create-article-model/basket.create.article.modal";
import { useVT } from "virtualizedtableforantd4";
import { ColumnsType } from "antd/lib/table";
import { displayCustomPrice } from "../../../../utils/helpers";
import i18n from "i18next";
import "./cart.create.table.scss";
import { Link } from "react-router-dom";
import {Price} from "../../../core/commons/price/price";
import {Export} from "../../../core/commons/export/export";
import {useCartText} from "../../cart.messages";

export const CartCreateTable: React.FC = observer(() => {
    const { store, controller } = useRequiredLayers<CartCreateStore, CartCreateController>(
        CartStoreContext,
        CartCreateControllerContext
    );
    const { t } = useTranslation("translation", { useSuspense: false });
    const text = useCartText();
    const [modalShown, setModalShown] = useState<boolean>(false);
    const [exportModalShown, setExportModalShown] = useState<boolean>(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        i18n.on("languageChanged", controller.onLngChange);
        return () => i18n.off("languageChanged", controller.onLngChange);
    }, []);

    const [form] = Form.useForm();
    const [editingKey, setEditingKey] = useState<string | number>("");
    const isCartEmpty = store.isEmpty;
    const searchQuery = search.trim().toLocaleLowerCase();
    const filteredTableData = store.tableData.filter(row =>
        row.id === "footer" || !searchQuery ||
        [row.article_name, row.article_description].some(value => value?.toLocaleLowerCase().includes(searchQuery))
    );
    const hasVisibleItems = filteredTableData.some(row => row.id !== "footer");

    useEffect(() => {
        if (isCartEmpty) {
            setSearch("");
            setEditingKey("");
        }
    }, [isCartEmpty]);

    const isRowDisabled = (row: CartCreateTableRecord): boolean => !!(!row.quantity.qty && row.id);

    const generateColumns = (): ColumnsType<CartCreateTableRecord> => {
        const columns: any[] = [
            {
                title: t("ARTICLES.TITLE_SINGLE"),
                dataIndex: "article_name",
                key: "article_name",
                width: "130px",
                render: (item: string, row: CartCreateTableRecord) => {
                    return item ? (
                        item.toUpperCase() === "DELIVERY" ? (
                            t("DELIVERY.TITLE")
                        ) : (
                            <Link
                                to={"/catalog/article/" + item}
                                className={isRowDisabled(row) ? "disabled link article-link" : "link article-link"}
                            >
                                {item}
                            </Link>
                        )
                    ) : null;
                },
            },
            {
                title: t("DESCRIPTION"),
                dataIndex: "article_description",
                key: "article_description",
                width: "230px",
                className: "cart-article-description-column",
                render: (item: string, row: CartCreateTableRecord) => (
                    <span className={isRowDisabled(row) ? "disabled" : undefined}>{item}</span>
                ),
            },
        ];

        columns.push({
            title: t("PRICE"),
            dataIndex: "price",
            key: "price",
            width: "90px",
            render: (item: CartCreateCalculatedPrice | undefined, row: CartCreateTableRecord) =>
                item ? (
                    <span className={isRowDisabled(row) ? "disabled" : undefined}>
                        {displayCustomPrice({
                            price: item.amount,
                        })}
                    </span>
                ) : null,
        });

        let usedWarehouses = store.warehousesAll;
        if (store.type === "reservation") {
            usedWarehouses = [];
        }

        usedWarehouses.forEach((warehouse) => {
            if (store.defaultWarehouses.includes(warehouse.id)) {
                columns.push({
                    title: warehouse.name,
                    dataIndex: warehouse.id,
                    key: warehouse.id,
                    width: "95px",
                    render: (item: {stockAmount: number, backorderAmount: number}, row: CartCreateTableRecord) => {
                        if (item) {
                            return (
                                <span className={isRowDisabled(row) ? "disabled" : undefined}>
                                    {item.stockAmount}
                                    {item.backorderAmount > 0 ? (
                                        <div className={"backorder-qty"}>
                                            +{item.backorderAmount}
                                            <span className={"descr"}> (backorder)</span>
                                        </div>
                                ) : null}
                                </span>
                            )
                        }
                        return '-'
                    },
                });
            }
        });

        columns.push({
            title: t("QUANTITY"),
            dataIndex: "quantity",
            key: "quantity",
            width: "100px",
            editable: true,
            render: (item: CartCreateOrderQuantity, row: CartCreateTableRecord) =>
                item ? (
                    <span className={isRowDisabled(row) ? "disabled" : undefined} style={{ whiteSpace: "nowrap" }}>
                        {item.qty} / {item.requestedQty}
                    </span>
                ) : null,
        });

        columns.push({
            title: t("TOTAL_AMOUNT"),
            dataIndex: "total_amount",
            key: "total_amount",
            width: "100px",
            render: (item: Amount | undefined, row: CartCreateTableRecord) =>
                item ? <span className={isRowDisabled(row) ? "disabled" : undefined}><Price value={item} /></span> : null,
        });

        columns.push({
            title: t("ACTION"),
            dataIndex: "action",
            key: "action",
            width: "80px",
            className: "cart-action-column",

            render: (_: any, record: CartCreateTableRecord & CartCreateTableLastRecord) => {
                const editable = isEditing(record);
                if (record.article_name) {
                    return (
                        <Space direction="horizontal" size={[5, 5]}>
                            {editable ? (
                                <Tooltip placement="topRight" title={t("SAVE")}>
                                    <Button
                                        className="btn"
                                        onClick={() => save(record)}
                                        type="default"
                                        size={"small"}
                                        icon={<FontAwesomeIcon icon={["fas", "check"]} />}
                                    />
                                </Tooltip>
                            ) : (
                                <Tooltip placement="topRight" title={t("EDIT")}>
                                    <Button
                                        className="btn"
                                        disabled={editingKey !== ""}
                                        onClick={() => edit(record)}
                                        type="default"
                                        size={"small"}
                                        icon={<FontAwesomeIcon icon={["fas", "pencil-alt"]} />}
                                    />
                                </Tooltip>
                            )}

                            <Popconfirm
                                title={t("ARE_YOU_SURE")}
                                onConfirm={() => controller.removeItem(record.id)}
                                placement="left"
                                okText={t("YES")}
                                cancelText={t("NO")}
                            >
                                <Tooltip placement="topRight" title={t("REMOVE")}>
                                    <Button
                                        className="btn-remove btn"
                                        disabled={editingKey !== ""}
                                        type="default"
                                        size={"small"}
                                        icon={<FontAwesomeIcon icon={["fas", "times"]} />}
                                    />
                                </Tooltip>
                            </Popconfirm>
                        </Space>
                    );
                }

                return <></>;
            },
        });
        return columns;
    };

    const isEditing = (record: CartCreateTableRecord & CartCreateTableLastRecord) => record.id === editingKey;

    const edit = (record: CartCreateTableRecord & CartCreateTableLastRecord & { id: React.Key }) => {
        form.setFieldsValue({ quantity: record.quantity.requestedQty });
        setEditingKey(record.id);
    };

    const save = async (record: CartCreateTableRecord & CartCreateTableLastRecord) => {
        try {
            const row = await form.validateFields();
            if (record.price.amount.value !== row.price) {
                await controller.updateItem(record.id, { qty: row.quantity });
            }
            setEditingKey("");
        } catch (errInfo) {
            message.error(t("ERROR"));
        }
    };

    const columns: any[] = generateColumns();

    const mergedColumns = columns.map((col: any) => {
        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (record: CartCreateTableRecord & CartCreateTableLastRecord) => ({
                record,
                inputType: "text",
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    const y = 500;
    const [vt, setComponent, vtRef] = useVT(() => ({ scroll: { y }, debug: true }), []);
    setComponent({
        body: {
            cell: EditableCell,
        },
    });

    useEffect(() => {
        vtRef.current?.scrollTo(0);
    }, [searchQuery, vtRef]);

    const tableContent = (
        <>
            <Space
                direction="vertical"
                style={{
                    width: "100%",
                    maxWidth: "100%",
                    overflowY: "auto",
                    marginBottom: 15,
                }}
            >
                <div className="cart-table-toolbar">
                    <Input
                        className="cart-table-search"
                        prefix={<FontAwesomeIcon icon={faSearch} />}
                        allowClear
                        value={search}
                        aria-label={text("CART_SEARCH")}
                        placeholder={text("CART_SEARCH")}
                        disabled={store.loading || editingKey !== ""}
                        onChange={event => setSearch(event.target.value)}
                    />
                    <div className={"card-table-buttons"}>
                        {store.loading ? (
                            <Space direction="horizontal" size={[10, 10]}>
                                <Skeleton.Button active />
                                <Skeleton.Button active />
                                <Skeleton.Button active />
                            </Space>
                        ) : (
                            <>
                                <Tooltip title={t("EXPORT.TITLE")}>
                                    <Button
                                        onClick={() => setExportModalShown(true)}
                                        loading={store.checkoutButtonDisabled}
                                        disabled={store.cart?.qty === 0}
                                        icon={<FontAwesomeIcon icon={["fas", "download"]} />}
                                    />
                                </Tooltip>
                                <Popconfirm
                                    title={t("ARE_YOU_SURE")}
                                    onConfirm={() => controller.clearCart()}
                                    placement="top"
                                    okText={t("YES")}
                                    cancelText={t("NO")}
                                    disabled={store.tableData.length === 1}
                                >
                                    <Tooltip title={t("CART.CLEAR")}>
                                        <Button
                                            disabled={store.tableData.length === 1}
                                            icon={<FontAwesomeIcon icon={["fas", "trash"]} />}
                                        />
                                    </Tooltip>
                                </Popconfirm>
                                <Button
                                    onClick={controller.createOrder}
                                    type="primary"
                                    loading={store.checkoutButtonDisabled}
                                    disabled={store.cart?.qty === 0}
                                    icon={<FontAwesomeIcon icon={["fas", "cart-shopping"]} />}
                                >
                                    &nbsp;{t("ORDERS.CHECKOUT")}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                {searchQuery ? (
                    <Alert
                        type="info"
                        showIcon
                        role="status"
                            description={`${text("CART_SEARCH_NOTICE")} ${text("CART_SEARCH_CLEAR_HINT")}`}
                    />
                ) : null}
                <Form form={form} component={false}>
                    <Table<CartCreateTableRecord>
                        className="basket-table"
                        rowKey={"id"}
                        components={hasVisibleItems ? vt : undefined}
                        columns={mergedColumns}
                        rowClassName="editable-row"
                        dataSource={hasVisibleItems ? filteredTableData : []}
                        loading={store.previewLoading || store.loading}
                        scroll={hasVisibleItems ? { x: true, y } : { x: true }}
                        locale={searchQuery ? { emptyText: text("CART_NO_MATCHES") } : undefined}
                        pagination={false}
                    />
                </Form>
            </Space>
            <Row justify={"center"}>
                {store.loading ? (
                    <Skeleton.Button active />
                ) : (
                    <Button type="primary" onClick={() => setModalShown(true)}>
                        {t("CART.ADD_ROW")}
                    </Button>
                )}
            </Row>
        </>
    );

    return (
        <>
            {isCartEmpty ? (
                <section className="cart-empty-state" aria-label={text("CART_EMPTY")}>
                    <div className="cart-empty-state-icon" aria-hidden="true">
                        <FontAwesomeIcon icon={faCartShopping} />
                    </div>
                    <h3>{text("CART_EMPTY")}</h3>
                    <p>{text("CART_EMPTY_HELP")}</p>
                    <Button
                        type="primary"
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        disabled={store.previewLoading}
                        onClick={() => setModalShown(true)}
                    >
                        {text("CART_ADD_ITEM")}
                    </Button>
                </section>
            ) : tableContent}
            {modalShown ? <CartCreateArticleModal onClose={() => setModalShown(false)} /> : null}
            {exportModalShown ? (
                <Export
                    title={"EXPORT.TITLE"}
                    isShown={exportModalShown}
                    withBasicExportFields
                    config={{
                        link: "/api/v3/cart",
                        query: {type: store.type, warehouses: store.defaultWarehouses, useBackorder: store.useBackorder}
                    }}
                    onClose={() => setExportModalShown(false)}
                />
            ) : null}
        </>
    );
});
